import { formatDate, getCalendarMonthRange } from "common";
import { Dayjs } from "dayjs";
import { useMemo } from "react";
import { AllowedPrivacy, DisplayType, Permission, PurchaseItemPrivacy, Timeslot } from "types";
import { useDebounce } from "use-debounce";
import { useSeller } from "./useAccount";
import { useAuthorization } from "./useAuthorization";
import { useAutoFetchCollection } from "./useAutoFetchCollection";

// TODO: WTF same logic as useArrivalTimeslots.ts
export const useTimeslots = ({
    product,
    quantity,
    start,
    showAllTimeslots,
    isPastAvailabilityChecked,
    privacy,
    isSingleDay,
    timezone,
}: useTimeslotsProps) => {
    const { isGranted } = useAuthorization();
    const seller = useSeller();

    const { datepickerPreference: datePickerPreference } = seller.preferences;
    const needsUnits = datePickerPreference.displayType === DisplayType.PRICE;

    const payload = useMemo(() => {
        const { start: startParam } = getCalendarMonthRange(start);
        const { end: endParam } = getCalendarMonthRange(start.add(1, "month"));

        const include = ["capacity.reserved", "event"];
        if (needsUnits) {
            // We only need units if the date picker is configured to show prices, otherwise it's a performance hit
            include.push("units");
        }

        if (isGranted(Permission.WAITLISTS_VIEW)) {
            include.push("waitlistCount");
        }

        const dateRange = isSingleDay
            ? [formatDate(start), formatDate(start)]
            : [formatDate(startParam), formatDate(endParam)];

        const payloadObject: any = {
            start: dateRange.join(","),
            product,
            include: include.filter(Boolean).join(","),
            limit: 500,
        };

        if (privacy) {
            payloadObject.privacy = privacy;
        }

        // For already created purchase, showAllTimeslots is not required and always false which doesn't give response for past dates
        if (!showAllTimeslots && !isPastAvailabilityChecked) {
            payloadObject["capacity.open[gt]"] = quantity - 1; // Because capacity endpoint does not have a gte operator
        }

        if (isPastAvailabilityChecked) {
            payloadObject.allowPastSlots = true;
        }

        if (timezone) {
            payloadObject.timezone = timezone;
        }

        return payloadObject;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formatDate(start), quantity, product, isPastAvailabilityChecked, privacy, timezone]); // Update when date/quantity/privacy/timezone changes

    const [debouncedPayload] = useDebounce(payload, 300); // Arrival picker should be quick so use the bare minimum debounce interval

    return useAutoFetchCollection<Timeslot>("timeslots", debouncedPayload, {
        keepPreviousData: true,
        refetchOnMount: true,
        staleTime: 1000, // Only re-fetch if the change happens after this time
    });
};

interface useTimeslotsProps {
    product: ID;
    quantity: number;
    start: Dayjs;
    showAllTimeslots: boolean;
    isPastAvailabilityChecked?: boolean;
    privacy?: AllowedPrivacy | PurchaseItemPrivacy;
    isSingleDay?: boolean;
    timezone?: string;
}
